import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { ScrollBox } from 'components/atoms/ScrollBox';
import styled from 'styled-components';
import { debounce } from 'throttle-debounce';
import { Box } from 'rebass/styled-components';
import { qsParse } from '../../../helpers/queryString';
import KeywordFilter from './keywordFilter';
import { Accordion } from '../contactsFilters/components/accordion';
import { createJobsGroups } from './jobsFilterGroups';
import { countJobsFilters } from './jobsFilterCount';

const AccordionContainer = styled.div`
  width: 100%;

  && label {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.main};
  }

  && > div > div:last-child {
    border-bottom: none;
  }
`;

export const JobsFilters = memo(
  ({
    jobsFilters,
    filterOrSortJobs,
    updateQueryString,
    sourceOptions,
    statusOptions,
    visibilityOptions,
    seniorityOptions,
    featuredOptions,
    expirationOptions,
    equityOptions,
    hideCompanyFilter,
    isCompanyProfile,
    isSourcingAgent,
  }) => {
    const location = useLocation();
    const currentSearch = qsParse(location).q || '';
    const [keywordValue, setKeywordValue] = useState(currentSearch);
    const isUpdatingFromUrl = useRef(false);

    // Debounced search handler
    const debouncedSearch = useMemo(
      () =>
        debounce(300, (value) => {
          isUpdatingFromUrl.current = true;
          filterOrSortJobs({ search: updateQueryString({ filter: 'q', value: value.trim() }) });
        }),
      [filterOrSortJobs, updateQueryString],
    );

    // Sync local state with URL when it changes externally (e.g., browser back button, filter reset)
    useEffect(() => {
      if (!isUpdatingFromUrl.current && currentSearch !== keywordValue) {
        // Cancel any pending debounced searches to prevent race condition
        debouncedSearch.cancel?.();
        setKeywordValue(currentSearch);
      }
      isUpdatingFromUrl.current = false;
    }, [currentSearch, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup debounce on unmount
    useEffect(
      () => () => {
        debouncedSearch.cancel?.();
      },
      [debouncedSearch],
    );

    const handleKeywordChange = useCallback(
      (e) => {
        const { value } = e.target;
        setKeywordValue(value); // Update local state immediately for responsive UI
        debouncedSearch(value); // Debounce the actual search
      },
      [debouncedSearch],
    );

    // setMultipleFilters function that converts filter object to URL params and calls filterOrSortJobs
    const setMultipleFilters = useCallback(
      (group, options = {}) => {
        const { removeFilters = [] } = options;
        const params = qsParse(location);
        const currentFilters = { ...jobsFilters };

        // Process each filter in the group
        Object.keys(group).forEach((key) => {
          const value = group[key];

          // Handle locations filter (with base64 encoding)
          if (key === 'locations' || key === 'locationsParam' || key === 'remote') {
            // Skip individual processing, handled below
            return;
          }

          // Handle salary filter (multiple fields)
          if (key === 'salary' && value && typeof value === 'object') {
            const { compensationAmountMinCents, currency, period } = value;
            if (compensationAmountMinCents) {
              params.compensationAmountMinCents = compensationAmountMinCents;
            } else {
              delete params.compensationAmountMinCents;
            }
            if (currency) {
              params.compensationCurrency = currency;
            } else {
              delete params.compensationCurrency;
            }
            if (period) {
              params.compensationPeriod = period;
            } else {
              delete params.compensationPeriod;
            }
            return;
          }

          // Handle date filters (addedAfter, addedBefore)
          if (key === 'addedAfter' || key === 'addedBefore') {
            if (value) {
              params[key] = value;
            } else {
              delete params[key];
            }
            return;
          }

          // Handle sourcingCampaignStatus as array (bracket notation handled by qsStringify)
          if (key === 'sourcingCampaignStatus') {
            if (Array.isArray(value) && value.length > 0) {
              params[key] = value; // qsStringify will convert to bracket notation
            } else {
              delete params[key];
            }
            return;
          }

          // Handle regular filters
          const normalizedValue = value && typeof value === 'object' && 'value' in value ? value.value : value;

          if (
            normalizedValue === 'any' ||
            normalizedValue === '' ||
            normalizedValue === null ||
            normalizedValue === undefined
          ) {
            delete params[key];
          } else {
            params[key] = normalizedValue.toString();
          }
        });

        // Handle locations and remote together
        if (group.locations !== undefined || group.locationsParam !== undefined || group.remote !== undefined) {
          const filterLocations = group.locations || currentFilters.locations || [];
          const remoteValue = group.remote !== undefined ? group.remote : currentFilters.remote || '';

          // If locationsParam is provided, use it directly
          if (group.locationsParam) {
            params.locations = group.locationsParam;
          } else if (filterLocations.length > 0) {
            // Filter out remote from locations array
            const locationsWithoutRemote = filterLocations.filter((item) => item.value !== 'remote');
            if (locationsWithoutRemote.length > 0) {
              params.locations = btoa(JSON.stringify(locationsWithoutRemote));
            } else {
              delete params.locations;
            }
          } else {
            delete params.locations;
          }

          // Handle remote flag
          if (remoteValue === 'remote') {
            params.remote = remoteValue;
          } else {
            delete params.remote;
          }
        }

        // Remove filters that should be deleted
        removeFilters.forEach((key) => {
          delete params[key];
        });

        filterOrSortJobs({ search: params });
      },
      [filterOrSortJobs, location, jobsFilters],
    );

    const groups = useMemo(
      () =>
        createJobsGroups({
          jobsFilters,
          sourceOptions,
          statusOptions,
          visibilityOptions,
          seniorityOptions,
          featuredOptions,
          expirationOptions,
          equityOptions,
          hideCompanyFilter,
          isCompanyProfile,
          setFilters: setMultipleFilters,
          isSourcingAgent,
        }).reduce((acc, { id: groupId, title, show, filters: groupFilters }) => {
          if (show) {
            const filtersToCount = {};
            const currentGroup = {
              id: groupId,
              title,
              content: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {groupFilters.reduce(
                    (acc2, { id: filterId, Component, props, show: showFilter, additionalCountKeys }) => {
                      if (showFilter) {
                        acc2.push(<Component key={filterId} {...props} />);

                        // Count filters for badge
                        const filterValue = jobsFilters[filterId];

                        if (filterValue && filterValue !== 'any') {
                          filtersToCount[filterId] = filterValue;
                        }

                        // Include additional filter keys for counting (e.g., for compound filters like salary)
                        if (additionalCountKeys) {
                          additionalCountKeys.forEach((key) => {
                            if (jobsFilters[key]) {
                              filtersToCount[key] = jobsFilters[key];
                            }
                          });
                        }
                      }

                      return acc2;
                    },
                    [],
                  )}
                </div>
              ),
            };

            const count = countJobsFilters(filtersToCount);
            if (count > 0) {
              currentGroup.badge = `${count}`;
            }

            acc.push(currentGroup);
          }

          return acc;
        }, []),
      [
        jobsFilters,
        sourceOptions,
        statusOptions,
        visibilityOptions,
        seniorityOptions,
        featuredOptions,
        expirationOptions,
        equityOptions,
        hideCompanyFilter,
        isCompanyProfile,
        setMultipleFilters,
        isSourcingAgent,
      ],
    );

    return (
      <ScrollBox
        data-testid="jobs-filters"
        sx={{
          top: 0,
          height: '100%',
          padding: '0 16px',
        }}
      >
        <Box
          sx={{
            gap: '12px',
            position: 'relative',
            paddingBottom: '16px',
          }}
        >
          <Box sx={{ width: '100%', mb: '12px' }} data-testid="jobs-filters-keywords">
            <KeywordFilter onChange={handleKeywordChange} value={keywordValue} showLabel={false} />
          </Box>
          <AccordionContainer>
            <Accordion items={groups} />
          </AccordionContainer>
        </Box>
      </ScrollBox>
    );
  },
);

JobsFilters.propTypes = {
  jobsFilters: PropTypes.object.isRequired,
  filterOrSortJobs: PropTypes.func.isRequired,
  updateQueryString: PropTypes.func.isRequired,
  sourceOptions: PropTypes.array,
  statusOptions: PropTypes.array,
  visibilityOptions: PropTypes.array,
  seniorityOptions: PropTypes.array,
  featuredOptions: PropTypes.array,
  expirationOptions: PropTypes.array,
  equityOptions: PropTypes.array,
  hideCompanyFilter: PropTypes.bool,
  isCompanyProfile: PropTypes.bool,
  isSourcingAgent: PropTypes.bool,
};

JobsFilters.defaultProps = {
  sourceOptions: [],
  statusOptions: [],
  visibilityOptions: [],
  seniorityOptions: [],
  featuredOptions: [],
  expirationOptions: [],
  equityOptions: [],
  hideCompanyFilter: false,
  isCompanyProfile: false,
  isSourcingAgent: false,
};
